import { useState } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { CreditCard, Wallet, MessageCircle, ChevronDown, ChevronUp, Sparkles } from "lucide-react";
import type { Invoice } from "@/types";
import { ACH_READY_STATUS } from "@/types";
import { differenceInDays } from "date-fns";

interface Recommendation {
  id: string;
  icon: React.ReactNode;
  title: string;
  detail: string;
  cta: string;
  onAction: () => void;
  savingsAmount?: number;
}

interface RecommendationsPanelProps {
  invoices: Invoice[];
  availableBudget: number;
  potentialSavings: number;
  /** Budget-blocked pending invoices */
  pendingInvoices: Invoice[];
  onPayClick: () => void;
  onBudgetClick: () => void;
}

function fmt(n: number) {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(n);
}

export function RecommendationsPanel({
  invoices,
  availableBudget: _availableBudget,
  potentialSavings,
  pendingInvoices,
  onPayClick,
  onBudgetClick,
}: RecommendationsPanelProps) {
  const [collapsed, setCollapsed] = useState(false);

  const recommendations: Recommendation[] = [];

  // 1. ACH-ready invoices — "Pay these now"
  const achReady = invoices.filter((i) => i.status === ACH_READY_STATUS);
  const achSavings = achReady.reduce((sum, i) => sum + (i.discount_amount || 0), 0);
  if (achReady.length > 0) {
    recommendations.push({
      id: "ach-ready",
      icon: <CreditCard className="w-4 h-4 text-success" />,
      title: `Pay ${achReady.length} accepted invoice${achReady.length !== 1 ? "s" : ""} — ${fmt(achSavings)} in savings ready`,
      detail: `${achReady.length} supplier${achReady.length !== 1 ? "s" : ""} accepted your offer. Generate the ACH file to capture these discounts before the payment window closes.`,
      cta: "Generate ACH & Pay",
      onAction: onPayClick,
      savingsAmount: achSavings,
    });
  }

  // 2. Budget-blocked invoices — "Unlock more savings"
  if (pendingInvoices.length > 0 && potentialSavings > 0) {
    const unlockAmount = pendingInvoices.reduce(
      (sum, i) => sum + (i.discounted_payment_amount || i.payment_amount),
      0
    );
    recommendations.push({
      id: "budget-blocked",
      icon: <Wallet className="w-4 h-4 text-warning" />,
      title: `Increase budget to unlock ${fmt(potentialSavings)} in blocked savings`,
      detail: `${pendingInvoices.length} invoice${pendingInvoices.length !== 1 ? "s" : ""} (${fmt(unlockAmount)} total) couldn't receive offers due to insufficient budget. Adding more unlocks them automatically.`,
      cta: "Increase Budget",
      onAction: onBudgetClick,
      savingsAmount: potentialSavings,
    });
  }

  // 3. Stale pending invoices — "Follow up with suppliers"
  const stalePending = invoices.filter((i) => {
    if (i.status !== "pending" || !i.email_sent_at) return false;
    return differenceInDays(new Date(), new Date(i.email_sent_at)) >= 5;
  });
  const staleSavings = stalePending.reduce((sum, i) => sum + (i.discount_amount || 0), 0);
  if (stalePending.length > 0) {
    const uniqueSuppliers = new Set(stalePending.map((i) => i.supplier_name)).size;
    recommendations.push({
      id: "stale-pending",
      icon: <MessageCircle className="w-4 h-4 text-primary" />,
      title: `Follow up with ${uniqueSuppliers} supplier${uniqueSuppliers !== 1 ? "s" : ""} — ${fmt(staleSavings)} waiting on a response`,
      detail: `${stalePending.length} offer${stalePending.length !== 1 ? "s" : ""} sent 5+ days ago with no response. A reminder email could recover these discounts before the window expires.`,
      cta: "View invoices",
      onAction: () => {}, // surfaced as info — user acts in the invoice list
      savingsAmount: staleSavings,
    });
  }

  if (recommendations.length === 0) return null;

  return (
    <Card className="glass-card border-primary/20 overflow-hidden">
      {/* Header */}
      <div
        className="flex items-center justify-between px-5 py-3 cursor-pointer select-none bg-gradient-to-r from-primary/8 to-transparent border-b border-primary/10"
        onClick={() => setCollapsed((c) => !c)}
      >
        <div className="flex items-center gap-2">
          <Sparkles className="w-4 h-4 text-primary" />
          <span className="text-sm font-semibold text-foreground">
            {recommendations.length} recommendation{recommendations.length !== 1 ? "s" : ""}
          </span>
          <Badge
            variant="outline"
            className="text-[10px] text-primary border-primary/30 px-1.5 py-0"
          >
            DiscoRun AI
          </Badge>
        </div>
        {collapsed ? (
          <ChevronDown className="w-4 h-4 text-muted-foreground" />
        ) : (
          <ChevronUp className="w-4 h-4 text-muted-foreground" />
        )}
      </div>

      {/* Cards */}
      {!collapsed && (
        <div className="divide-y divide-border/50">
          {recommendations.map((rec) => (
            <div key={rec.id} className="flex items-start gap-4 px-5 py-4">
              <div className="w-8 h-8 rounded-lg bg-muted/60 flex items-center justify-center flex-shrink-0 mt-0.5">
                {rec.icon}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-foreground leading-snug">{rec.title}</p>
                <p className="text-xs text-muted-foreground mt-1 leading-relaxed">{rec.detail}</p>
              </div>
              {rec.id !== "stale-pending" && (
                <Button
                  size="sm"
                  variant={rec.id === "ach-ready" ? "default" : "outline"}
                  className="h-8 text-xs flex-shrink-0"
                  onClick={(e) => {
                    e.stopPropagation();
                    rec.onAction();
                  }}
                >
                  {rec.cta}
                </Button>
              )}
            </div>
          ))}
        </div>
      )}
    </Card>
  );
}
